<template>
  <div v-if="payments.length > 0">
    <h3 class="mb-2">
      {{ $t('stripe_payment.history') }}
      <v-icon small class="mx-2" color="primary" @click="$emit('refresh-payments')">
        mdi-refresh
      </v-icon>
    </h3>
    <v-card flat>
      <v-data-table
        :headers="headers"
        :items="payments"
        :loading="isLoading"
        class="elevation-0"
        :items-per-page="10"
        :footer-props="tableFooterProps([5, 10, 20, 50])"
      >
        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:item.amount="{ item }">
          {{ formatAmount(item.amount) }} {{ item.currency || currency }}
        </template>
        <template v-slot:item.refunded_amount="{ item }">
          <span v-if="item.refunded_amount > 0">
            {{ formatAmount(item.refunded_amount) }} {{ item.currency || currency }}
          </span>
          <span v-else>-</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="getStatusColor(item.status)" dark>
            {{ translateStatus(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.payment_url="{ item }">
          <v-btn icon v-if="item.payment_url" @click="copyToClipboard(item.payment_url)" :title="$t('common.copy')">
            <v-icon color="primary">mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon 
            v-if="item.payment_url"
            @click="viewPayment(item)" 
            :title="$t('stripe_payment.view_payment')">
            <v-icon color="info">mdi-eye</v-icon>
          </v-btn>
          <v-btn icon 
            :disabled="item.status !== 'success' || item.refunded_amount >= item.amount" 
            @click="refundPayment(item)" 
            :title="$t('stripe_payment.refund')">
            <v-icon color="error">mdi-cash-refund</v-icon>
          </v-btn>
        </template>
        <template v-slot:no-data>
          {{ $t('stripe_payment.no_payments') }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { copyToClipboard as copyTextToClipboard } from '@/utils/clipboard';

export default {
  name: 'StripePaymentsTable',
  props: {
    payments: {
      type: Array,
      default: () => [],
    },
    currency: {
      type: String,
      default: 'RUB',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headers() {
      return [
        { text: this.$t('stripe_payment.id'), value: 'id', align: 'center' },
        { text: this.$t('stripe_payment.created'), value: 'created_at', align: 'center' },
        { text: this.$t('stripe_payment.amount'), value: 'amount', align: 'center' },
        { text: this.$t('stripe_payment.refunded'), value: 'refunded_amount', align: 'center' },
        { text: this.$t('stripe_payment.order_id'), value: 'order_id', align: 'center' },
        { text: this.$t('stripe_payment.status'), value: 'status', align: 'center' },
        { text: this.$t('stripe_payment.payment_url'), value: 'payment_url', align: 'center' },
        { text: this.$t('common.actions'), value: 'actions', align: 'center', sortable: false },
      ];
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString(this.$i18n.locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatAmount(value) {
      return parseFloat(value).toFixed(2);
    },
    translateStatus(status) {
      const translations = {
        created: this.$t('stripe_payment.status_created'),
        pending: this.$t('stripe_payment.status_pending'),
        success: this.$t('stripe_payment.status_success'),
        failed: this.$t('stripe_payment.status_failed'),
        cancelled: this.$t('stripe_payment.status_cancelled'),
        refunded: this.$t('stripe_payment.status_refunded'),
        partially_refunded: this.$t('stripe_payment.status_partially_refunded'),
      };
      return translations[status] || status;
    },
    getStatusColor(status) {
      const colors = {
        created: 'info',
        pending: 'warning',
        success: 'success',
        failed: 'error',
        cancelled: 'grey',
        refunded: 'orange',
        partially_refunded: 'orange',
      };
      return colors[status] || 'info';
    },
    async copyToClipboard(text) {
      try {
        await copyTextToClipboard(text);
        this.$notify(this.$t("common.copied"), "", "success", 3000);
      } catch (error) {
        this.$notify(this.$t("custom.copy_error"), "", "error");
      }
    },
    viewPayment(payment) {
      this.$emit('view-payment', payment);
    },
    refundPayment(payment) {
      this.$emit('refund-payment', payment);
    },
  },
};
</script>

<style scoped>
</style>

